import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../api';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Button
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import { format, addDays, differenceInDays } from 'date-fns';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeCases: 0,
    totalDocuments: 0,
    researchDocs: 0,
    teamMembers: 0
  });
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [researchDocs, setResearchDocs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch all data in parallel
      const [casesRes, researchRes, teamRes] = await Promise.all([
        apiService.getCases(),
        apiService.getResearchDocuments().catch(() => ({ data: { documents: [] } })),
        apiService.getTeamMembers().catch(() => ({ data: { members: [] } }))
      ]);
      
      const allCases = casesRes.data.cases || [];
      const research = researchRes.data.documents || [];
      const team = teamRes.data.members || [];
      
      setCases(allCases);
      setResearchDocs(research.slice(0, 3)); // Top 3 for dashboard
      setTeamMembers(team);
      
      // Calculate total documents across all cases
      let totalDocs = 0;
      for (const caseItem of allCases) {
        try {
          const docsRes = await apiService.getCaseDocuments(caseItem.id);
          totalDocs += (docsRes.data.documents || []).length;
        } catch (err) {
          console.error(`Error fetching documents for case ${caseItem.id}:`, err);
        }
      }
      
      // Build recent activity from analyses and documents
      const activities = [];
      
      for (const caseItem of allCases.slice(0, 3)) {
        try {
          // Get recent documents
          const docsRes = await apiService.getCaseDocuments(caseItem.id);
          const docs = docsRes.data.documents || [];
          
          docs.slice(0, 2).forEach(doc => {
            activities.push({
              action: 'document_upload',
              description: `${doc.filename} uploaded to ${caseItem.title}`,
              timestamp: doc.uploaded_at || doc.created_at,
              case_id: caseItem.id
            });
          });
          
          // Get recent analyses
          try {
            const analysesRes = await apiService.getCaseAnalyses(caseItem.id);
            const analyses = analysesRes.data.analyses || [];
            
            analyses.slice(0, 2).forEach(analysis => {
              activities.push({
                action: 'analysis',
                description: `${analysis.analysis_type || 'Legal'} analysis completed for ${caseItem.title}`,
                timestamp: analysis.created_at,
                case_id: caseItem.id
              });
            });
          } catch (err) {
            console.error(`Error fetching analyses for case ${caseItem.id}:`, err);
          }
        } catch (err) {
          console.error(`Error fetching documents for case ${caseItem.id}:`, err);
        }
      }
      
      // Sort by timestamp and take top 5
      activities.sort((a, b) => {
        const dateA = a.timestamp?.seconds ? new Date(a.timestamp.seconds * 1000) : new Date(a.timestamp || 0);
        const dateB = b.timestamp?.seconds ? new Date(b.timestamp.seconds * 1000) : new Date(b.timestamp || 0);
        return dateB - dateA;
      });
      
      setRecentActivity(activities.slice(0, 5));
      
      // Fetch upcoming schedules
      const allSchedules = [];
      
      for (const caseItem of allCases.slice(0, 5)) {
        try {
          const schedulesRes = await apiService.getCaseSchedules(caseItem.id);
          const schedules = schedulesRes.data.schedules || [];
          
          schedules.forEach(schedule => {
            // Parse date - schedules use 'scheduled_date' field
            const dateField = schedule.scheduled_date || schedule.date;
            
            let scheduleDate = null;
            if (dateField?.seconds) {
              scheduleDate = new Date(dateField.seconds * 1000);
            } else if (dateField?.toDate) {
              scheduleDate = dateField.toDate();
            } else if (dateField) {
              scheduleDate = new Date(dateField);
            }
            
            // Check if date is valid
            if (!scheduleDate || isNaN(scheduleDate.getTime())) {
              return;
            }
            
            const daysUntil = differenceInDays(scheduleDate, new Date());
            
            // Include all future events (no upper limit) for dashboard visibility
            if (daysUntil >= 0) {
              allSchedules.push({
                ...schedule,
                title: schedule.description || schedule.type || 'Untitled Schedule',
                case_title: caseItem.title,
                case_id: caseItem.id,
                court_date: scheduleDate,
                daysUntil
              });
            }
          });
        } catch (err) {
          console.error(`Error fetching schedules for case ${caseItem.id}:`, err);
        }
      }
      
      // Sort schedules by date and take top 4
      allSchedules.sort((a, b) => a.court_date - b.court_date);
      setUpcomingSchedules(allSchedules.slice(0, 4));
      
      // Calculate stats
      const activeCases = allCases.filter(c => c.status === 'open' || c.status === 'in-progress').length;
      
      setStats({
        activeCases,
        totalDocuments: totalDocs,
        researchDocs: research.length,
        teamMembers: team.length
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil <= 2) return 'error';      // Red: Very urgent (0-2 days)
    if (daysUntil <= 7) return 'warning';    // Yellow: Upcoming (3-7 days)
    if (daysUntil <= 30) return 'info';      // Blue: Soon (8-30 days)
    return 'default';                        // Gray: Future (30+ days)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'info';
      case 'in-progress': return 'warning';
      case 'closed': return 'success';
      default: return 'default';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
        Command Center
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back! Here's your practice overview.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ bgcolor: 'primary.main', color: 'white', boxShadow: 3, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}
            onClick={() => navigate('/cases')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.activeCases}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Cases
                  </Typography>
                </Box>
                <FolderIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ bgcolor: 'warning.main', color: 'white', boxShadow: 3, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}
            onClick={() => navigate('/cases')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.totalDocuments}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Documents
                  </Typography>
                </Box>
                <EventIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ bgcolor: 'success.main', color: 'white', boxShadow: 3, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}
            onClick={() => navigate('/research-vault')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.researchDocs}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Research Documents
                  </Typography>
                </Box>
                <LibraryBooksIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ bgcolor: 'info.main', color: 'white', boxShadow: 3, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}
            onClick={() => navigate('/team')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.teamMembers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Team Members
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Upcoming Court Dates */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon color="primary" />
                  Upcoming Court Dates
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/cases')}>
                  View All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {upcomingSchedules.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No upcoming court dates
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add schedules to your cases to see upcoming dates
                  </Typography>
                </Box>
              ) : (
                <List>
                  {upcomingSchedules.map((schedule, index) => {
                    return (
                      <ListItem
                        key={`${schedule.case_id}-${index}`}
                        sx={{
                          borderLeft: 4,
                          borderColor: `${getUrgencyColor(schedule.daysUntil)}.main`,
                          mb: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                        onClick={() => navigate(`/case/${schedule.case_id}`)}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {schedule.case_title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {schedule.title || schedule.type || 'Court Appearance'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={schedule.daysUntil === 0 ? 'Today' : schedule.daysUntil === 1 ? 'Tomorrow' : `${schedule.daysUntil} days`}
                                  size="small"
                                  color={getUrgencyColor(schedule.daysUntil)}
                                  sx={{ height: 20 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {format(schedule.court_date, 'MMM dd, yyyy')}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon color="primary" />
                  Recent Activity
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/team')}>
                  View All
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {recentActivity.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SmartToyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Team activities will appear here
                  </Typography>
                </Box>
              ) : (
                <List>
                  {recentActivity.map((activity, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        mb: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.100' }
                      }}
                    >
                      <ListItemIcon>
                        {activity.action === 'document_upload' && <DescriptionIcon color="primary" />}
                        {activity.action === 'case_update' && <FolderIcon color="primary" />}
                        {activity.action === 'analysis' && <SmartToyIcon color="primary" />}
                        {!['document_upload', 'case_update', 'analysis'].includes(activity.action) && <SmartToyIcon color="primary" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.description || activity.action}
                        secondary={formatTimeAgo(activity.timestamp)}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;